import * as React from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { SurfaceList } from "./SurfaceList";
import { MaskEditor } from "./MaskEditor";
import { AddSurfaceDialog } from "./AddSurfaceDialog";
import { useSpaceStore } from "../../stores/spaceStore";
import { deleteSurface, getAvailableSurfaces, recomputeGeometry } from "../../services/api";
import { useToast } from "../ui/Toast";
import type { Surface } from "../../types";

export function SurfaceManager() {
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isRecomputing, setIsRecomputing] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState<Surface | null>(null);

  const { selectedSurface, setSelectedSurface, setSurfaces, spaceId } = useSpaceStore();
  const { addToast } = useToast();

  const handleDeleteSurface = async (surface: Surface) => {
    if (!deleteConfirm) {
      setDeleteConfirm(surface);
      return;
    }

    try {
      const response = await deleteSurface(surface.type, surface.index, spaceId || undefined);

      if (response.status === "SUCCESS") {
        // Clear selection if the deleted surface was selected
        if (selectedSurface?.type === surface.type && selectedSurface?.index === surface.index) {
          setSelectedSurface(null);
        }
        // Refresh surfaces list
        const surfacesResponse = await getAvailableSurfaces(spaceId || undefined);
        if (surfacesResponse.surfaces) {
          setSurfaces(surfacesResponse.surfaces);
        }
        addToast("success", `Deleted ${surface.label}`);
      } else {
        throw new Error(response.message || "Failed to delete surface");
      }
    } catch (error) {
      addToast("error", "Failed to delete surface");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleRecomputeGeometry = async () => {
    setIsRecomputing(true);
    try {
      const response = await recomputeGeometry(spaceId || undefined);

      if (response.status === "SUCCESS") {
        addToast("success", "Geometry recomputed successfully");
        // Refresh surfaces
        const surfacesResponse = await getAvailableSurfaces(spaceId || undefined);
        if (surfacesResponse.surfaces) {
          setSurfaces(surfacesResponse.surfaces);
        }
      } else {
        throw new Error(response.message || "Failed to recompute geometry");
      }
    } catch (error) {
      addToast("error", "Failed to recompute geometry");
    } finally {
      setIsRecomputing(false);
    }
  };

  const handleSurfaceAdded = async () => {
    setIsAddDialogOpen(false);
    // Refresh surfaces list
    try {
      const response = await getAvailableSurfaces(spaceId || undefined);
      if (response.surfaces) {
        setSurfaces(response.surfaces);
      }
    } catch (error) {
      console.error("Failed to refresh surfaces:", error);
    }
  };

  if (!spaceId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Load a space first to manage surfaces
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRecomputeGeometry}
          loading={isRecomputing}
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Recompute Geometry
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
        {/* Surface List */}
        <div className="xl:col-span-1">
          <Card>
            <CardContent className="pt-6">
              <SurfaceList
                onAddSurface={() => setIsAddDialogOpen(true)}
                onDeleteSurface={handleDeleteSurface}
              />
            </CardContent>
          </Card>
        </div>

        {/* Mask and Marker Editor */}
        <div className="xl:col-span-3">
          {selectedSurface ? (
            <MaskEditor surface={selectedSurface} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                Select a surface to view its mask and marker
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Delete Surface</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Are you sure you want to delete <strong>{deleteConfirm.label}</strong>?
                This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteSurface(deleteConfirm)}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Surface Dialog */}
      <AddSurfaceDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSuccess={handleSurfaceAdded}
      />
    </div>
  );
}
